import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { apiClient } from "@/common/apiClient";
import { toast } from "sonner";

interface UserAssessment {
  answer: string | null;
  score: number | null;
  answeredAt: string | null;
}

interface Response {
  questionId: string;
  questionText: string;
  questionListId: string;
  order: number;
  userAssessment: UserAssessment;
}

interface UserKPIForm {
  _id: string;
  kpiFormTitle: string;
  status: string;
  responses: Response[];
}

interface QuestionListGroup {
  questionListId: string;
  questions: Response[];
}

export const FormFillPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<UserKPIForm | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savingListId, setSavingListId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (id) {
      fetchForm();
    }
  }, [id]);

  const fetchForm = async () => {
    try {
      const response = await apiClient.get(`/api/user-kpi-form/${id}`);
      if (response.success) {
        setForm(response.data);
      } else {
        toast.error("Failed to load form");
      }
    } catch (error) {
      toast.error("Failed to load form");
    } finally {
      setIsLoading(false);
    }
  };

  const groupByQuestionList = (responses: Response[]): QuestionListGroup[] => {
    const grouped = responses.reduce((acc, response) => {
      const listId = response.questionListId;
      if (!acc[listId]) {
        acc[listId] = [];
      }
      acc[listId].push(response);
      return acc;
    }, {} as Record<string, Response[]>);

    return Object.entries(grouped).map(([questionListId, questions]) => ({
      questionListId,
      questions: questions.sort((a, b) => a.order - b.order),
    }));
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    if (!form) return;

    const updatedResponses = form.responses.map((r) =>
      r.questionId === questionId
        ? {
            ...r,
            userAssessment: {
              ...r.userAssessment,
              answer: value,
              answeredAt: new Date().toISOString(),
            },
          }
        : r
    );

    setForm({ ...form, responses: updatedResponses });
  };

  const handleScoreChange = (questionId: string, value: string) => {
    if (!form) return;

    const updatedResponses = form.responses.map((r) =>
      r.questionId === questionId
        ? {
            ...r,
            userAssessment: {
              ...r.userAssessment,
              score: parseInt(value),
              answeredAt: new Date().toISOString(),
            },
          }
        : r
    );

    setForm({ ...form, responses: updatedResponses });
  };

  const handleSaveQuestionList = async (questionListId: string) => {
    if (!form) return;

    setSavingListId(questionListId);
    try {
      const response = await apiClient.put(`/api/user-kpi-form/${id}`, {
        responses: form.responses,
      });

      if (response.success) {
        toast.success("Saved successfully");
        fetchForm();
      } else {
        toast.error("Failed to save");
      }
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSavingListId(null);
    }
  };

  const handleSubmitAll = async () => {
    if (!form) return;

    setIsSubmitting(true);
    try {
      const response = await apiClient.post(`/api/user-kpi-form/${id}/submit`);

      if (response.success) {
        toast.success("Form submitted successfully");
        navigate("/my-kpi-forms");
      } else {
        toast.error("Failed to submit form");
      }
    } catch (error) {
      toast.error("Failed to submit form");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="p-4">Loading...</div>;
  }

  if (!form) {
    return <div className="p-4">Form not found</div>;
  }

  const questionListGroups = groupByQuestionList(form.responses);
  const totalSteps = questionListGroups.length + 1; // sections + submit step

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto p-6">
        <div className="bg-white rounded-lg border shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {form.kpiFormTitle}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Complete your KPI assessment
              </p>
            </div>
            <div>
              <span
                className={`px-3 py-1 rounded-full text-xs font-medium ${
                  form.status === "submitted"
                    ? "bg-green-100 text-green-800"
                    : "bg-blue-100 text-blue-800"
                }`}
              >
                {form.status === "submitted" ? "Completed" : "In Progress"}
              </span>
            </div>
          </div>
          <div className="flex items-center justify-between pt-4 border-t">
            <div>
              <h3 className="font-semibold text-base">Ready to submit?</h3>
              <p className="text-sm text-gray-500 mt-1">
                Once submitted, your KPI form will be locked and cannot be
                edited
              </p>
            </div>
            <Button
              onClick={handleSubmitAll}
              disabled={isSubmitting || form.status === "submitted"}
              size="lg"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex gap-2 mb-6">
          {questionListGroups.map((group, index) => (
            <Button
              key={group.questionListId}
              variant={currentStep === index ? "default" : "outline"}
              size="sm"
              onClick={() => setCurrentStep(index)}
            >
              {index + 1}
            </Button>
          ))}
          <Button
            variant={
              currentStep === questionListGroups.length ? "default" : "outline"
            }
            size="sm"
            onClick={() => setCurrentStep(questionListGroups.length)}
          >
            Submit
          </Button>
        </div>

        {/* Section Content */}
        {currentStep < questionListGroups.length ? (
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Section {currentStep + 1}</h2>
            </div>

            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {questionListGroups[currentStep].questions.map(
                (question, qIndex) => (
                  <div
                    key={question.questionId}
                    className="rounded-lg p-5 border space-y-4"
                  >
                    <div>
                      <h3 className="font-semibold text-gray-900 text-base leading-relaxed">
                        {qIndex + 1}. {question.questionText}
                      </h3>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 block">
                        Your Answer
                      </label>
                      <Textarea
                        value={question.userAssessment.answer || ""}
                        onChange={(e) =>
                          handleAnswerChange(
                            question.questionId,
                            e.target.value
                          )
                        }
                        placeholder="Enter your answer here..."
                        className="bg-white"
                        rows={4}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700 block">
                        Self Score (1-5)
                      </label>
                      <div className="flex gap-1.5 flex-wrap">
                        {[
                          { score: 1, label: "Unacceptable" },
                          { score: 2, label: "Need Improvement" },
                          { score: 3, label: "Meet Expectations" },
                          { score: 4, label: "Exceed Expectations" },
                          { score: 5, label: "Outstanding" },
                        ].map(({ score, label }) => (
                          <Button
                            key={score}
                            type="button"
                            variant={
                              question.userAssessment.score === score
                                ? "default"
                                : "outline"
                            }
                            size="sm"
                            className="flex-col h-auto py-1.5 px-2.5 text-xs"
                            onClick={() =>
                              handleScoreChange(
                                question.questionId,
                                score.toString()
                              )
                            }
                          >
                            <div className="font-semibold text-sm">{score}</div>
                            <div className="text-[10px] font-normal leading-tight">
                              {label}
                            </div>
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              )}
            </div>

            <div className="flex justify-between mt-6 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
                disabled={currentStep === 0}
              >
                Previous
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() =>
                    handleSaveQuestionList(
                      questionListGroups[currentStep].questionListId
                    )
                  }
                  disabled={
                    savingListId ===
                    questionListGroups[currentStep].questionListId
                  }
                >
                  {savingListId ===
                  questionListGroups[currentStep].questionListId
                    ? "Saving..."
                    : "Save Section"}
                </Button>
                <Button
                  onClick={() =>
                    setCurrentStep(Math.min(totalSteps - 1, currentStep + 1))
                  }
                >
                  {currentStep === questionListGroups.length - 1
                    ? "Review & Submit"
                    : "Next"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">Review & Submit</h2>
            <p className="text-gray-600 mb-6">
              Please review all sections before submitting. Once submitted, your
              KPI form will be locked.
            </p>

            <div className="space-y-4 mb-6">
              {questionListGroups.map((group, index) => (
                <div
                  key={group.questionListId}
                  className="flex items-center justify-between p-4 border rounded-lg"
                >
                  <div>
                    <h3 className="font-semibold">Section {index + 1}</h3>
                    <p className="text-sm text-gray-500">
                      {group.questions.length} questions
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentStep(index)}
                  >
                    Review
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex justify-between pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setCurrentStep(currentStep - 1)}
              >
                Previous
              </Button>
              <Button
                onClick={handleSubmitAll}
                disabled={isSubmitting || form.status === "submitted"}
                size="lg"
              >
                {isSubmitting ? "Submitting..." : "Submit KPI Form"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
